import React, { useState } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

interface IcpGoalStepProps {
  businessName: string;
  industry: string;
  icpGuess: string;
  newsletterGoal: string;
  voiceAndTone: string;
  onConfirm: (icp: string, goal: string, voiceAndTone: string) => void;
  isLoading: boolean;
}

type Field = "icp" | "goal" | "voice";

const MicButton: React.FC<{ field: Field; activeField: Field | null; onToggle: (field: Field) => void; listening: boolean }> = ({ field, activeField, onToggle, listening }) => {
  const isActive = listening && activeField === field;
  return (
    <button
      type="button"
      onClick={() => onToggle(field)}
      title={isActive ? "Stop dictation" : "Dictate"}
      style={{
        position: "absolute",
        right: "10px",
        top: "10px",
        background: isActive ? "#ef4444" : "#f0f0f0",
        border: "none",
        borderRadius: "50%",
        width: "28px",
        height: "28px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        color: isActive ? "#fff" : "#666",
      }}
    >
      {isActive ? "..." : "\u{1F3A4}"}
    </button>
  );
};

const IcpGoalStep: React.FC<IcpGoalStepProps> = ({
  businessName,
  industry,
  icpGuess,
  newsletterGoal,
  voiceAndTone,
  onConfirm,
  isLoading,
}) => {
  const [icp, setIcp] = useState(icpGuess);
  const [goal, setGoal] = useState(newsletterGoal);
  const [voice, setVoice] = useState(voiceAndTone);
  const [activeField, setActiveField] = useState<Field | null>(null);

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  React.useEffect(() => {
    if (transcript && activeField) {
      const setter = activeField === "icp" ? setIcp : activeField === "goal" ? setGoal : setVoice;
      setter((prev) => prev + " " + transcript);
      resetTranscript();
    }
  }, [transcript, activeField, resetTranscript]);

  const toggleSpeech = (field: Field) => {
    if (listening && activeField === field) {
      SpeechRecognition.stopListening();
      setActiveField(null);
    } else {
      if (listening) SpeechRecognition.stopListening();
      setActiveField(field);
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
  };

  return (
    <div style={{ maxWidth: "540px", width: "100%", textAlign: "left" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem", textAlign: "center" }}>
        We analyzed {businessName}
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem", textAlign: "center", fontSize: "0.9rem" }}>
        Industry: {industry}. Review and edit these settings before we continue.
      </p>

      <div style={{ marginBottom: "1.25rem" }}>
        <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#888", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Who reads this newsletter?
        </label>
        <div style={{ position: "relative" }}>
          <textarea
            value={icp}
            onChange={(e) => setIcp(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              padding: "12px 44px 12px 14px",
              fontSize: "0.95rem",
              border: "1px solid #ddd",
              borderRadius: "10px",
              resize: "vertical",
              lineHeight: 1.5,
              boxSizing: "border-box",
            }}
          />
          {browserSupportsSpeechRecognition && (
            <MicButton field="icp" activeField={activeField} onToggle={toggleSpeech} listening={listening} />
          )}
        </div>
      </div>

      <div style={{ marginBottom: "1.25rem" }}>
        <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#888", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Newsletter Goal
        </label>
        <div style={{ position: "relative" }}>
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              padding: "12px 44px 12px 14px",
              fontSize: "0.95rem",
              border: "1px solid #ddd",
              borderRadius: "10px",
              resize: "vertical",
              lineHeight: 1.5,
              boxSizing: "border-box",
            }}
          />
          {browserSupportsSpeechRecognition && (
            <MicButton field="goal" activeField={activeField} onToggle={toggleSpeech} listening={listening} />
          )}
        </div>
      </div>

      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#888", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Voice & Tone
        </label>
        <div style={{ position: "relative" }}>
          <textarea
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              padding: "12px 44px 12px 14px",
              fontSize: "0.95rem",
              border: "1px solid #ddd",
              borderRadius: "10px",
              resize: "vertical",
              lineHeight: 1.5,
              boxSizing: "border-box",
            }}
          />
          {browserSupportsSpeechRecognition && (
            <MicButton field="voice" activeField={activeField} onToggle={toggleSpeech} listening={listening} />
          )}
        </div>
      </div>

      <button
        onClick={() => { if (listening) SpeechRecognition.stopListening(); onConfirm(icp, goal, voice); }}
        disabled={!icp.trim() || !goal.trim() || isLoading}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap", maxWidth: "none",
          opacity: !icp.trim() || !goal.trim() || isLoading ? 0.5 : 1,
        }}
      >
        Looks Good →
      </button>
    </div>
  );
};

export default IcpGoalStep;
